import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Volume2, Play } from "lucide-react";
import AnimatedBackground from "@/components/AnimatedBackground";
import { useIsMobile } from "@/hooks/use-mobile";
import SectionHeader from "@/components/SectionHeader";


const TraditionalProductions = () => {
  const [activeVideo, setActiveVideo] = useState(0);
  const [videoWithSound, setVideoWithSound] = useState(false);
  const isMobile = useIsMobile();

  const handleVideoClick = () => {
    setVideoWithSound(true);
  };
  const handleVideoChange = (index: number) => {
    setActiveVideo(index);
    setVideoWithSound(false);
  };

  // Vimeo video IDs with branch names
  const videos = [{
    id: "1089155100",
    title: "TMD Friction Services GmbH",
    mobileTitle: "Webagentur"
  }, {
    id: "1065378871",
    title: "A-Z Dellen- und Hagelschadenreparatur GmbH",
    mobileTitle: "Dellendoktor"
  }, {
    id: "1137933071",
    title: "Jofo Pneumatik GmbH",
    mobileTitle: "Pneumatik"
  }];

  return (
    <section id="work" className="py-12 md:py-20 lg:py-24 bg-gradient-to-b from-background/90 via-background/95 to-background relative overflow-hidden">
      {/* Animated Background with Green Dots */}
      <AnimatedBackground variant="green" />
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <SectionHeader
          badge="Klassische Filmproduktion"
          title="Imagefilme & Testimonials"
          highlightedWord="Testimonials"
          description="Cinematisch. Emotional. Authentisch. Echte Filmproduktionen mit echten Menschen und echten Geschichten."
          className="mb-8 md:mb-12"
        />

        {/* Video Player */}
        <div className="max-w-5xl mx-auto mb-6 md:mb-8 px-4 md:px-0">
          <div className="aspect-video bg-muted rounded-lg overflow-hidden shadow-2xl animate-fade-in group cursor-pointer relative" onClick={handleVideoClick}>
            {/* Thumbnail Fallback - visible until video plays on mobile */}
            <div 
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-500 ${(isMobile && !videoWithSound) ? 'opacity-100' : (!isMobile ? 'opacity-0' : 'opacity-0')}`}
              style={{ backgroundImage: `url(https://vumbnail.com/${videos[activeVideo].id}.jpg)` }}
            />
            {/* On mobile: only load iframe after tap; on desktop: always load */}
            {(!isMobile || videoWithSound) && (
              <iframe 
                key={`${activeVideo}-${videoWithSound ? 'sound' : 'muted'}`} 
                src={`https://player.vimeo.com/video/${videos[activeVideo].id}?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&loop=1&muted=${videoWithSound ? '0' : '1'}&dnt=1`} 
                className="w-full h-full relative z-10" 
                frameBorder="0" 
                allow="autoplay; fullscreen; picture-in-picture" 
                title={`Imagefilm Aachen - ${videos[activeVideo].title}`}
              />
            )}
            {/* Play/Sound overlay */}
            {!videoWithSound && (
              <div className={`absolute inset-0 flex items-center justify-center z-20 ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity duration-300`}>
                <div className="bg-background/80 backdrop-blur-sm rounded-full p-4">
                  {isMobile ? <Play className="w-6 h-6 text-accent fill-accent" /> : <Volume2 className="w-6 h-6 text-accent" />}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Project Selector */}
        <div className="flex justify-center animate-fade-in px-4">
          <div className="inline-flex items-center bg-muted/50 backdrop-blur-sm rounded-full p-1 border border-accent/20">
            {videos.map((video, index) => (
              <button 
                key={index} 
                onClick={() => handleVideoChange(index)} 
                className={`relative px-4 md:px-6 py-2 md:py-2.5 text-xs md:text-sm rounded-full transition-all duration-300 ${
                  activeVideo === index 
                    ? "bg-accent text-background font-semibold shadow-lg" 
                    : "text-foreground/70 md:hover:text-foreground"
                }`}
              >
                {isMobile ? video.mobileTitle : video.title}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TraditionalProductions;